/**
 * Sentry Configuration for LegalSyncUp
 * Swiss Legal Compliance Platform - Error monitoring, performance tracking, and logging
 * Configured for Swiss data protection laws (nDSG/GDPR compliance)
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry with LegalSyncUp configuration
 */
export function initSentry() {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,

    // GDPR/nDSG: do not send PII (IP, email) automatically
    sendDefaultPii: false,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],

    // Tracing
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in development

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/legalsyncup\.com/,
      /^https:\/\/.*\.legalsyncup\.com/,
      /^https:\/\/.*\.habitus\.workers\.dev/
    ],

    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Environment configuration
    environment: import.meta.env.MODE || 'development',

    // Release tracking with git commit information
    release: (() => {
      // First try to get from build-time injection
      if (typeof __RELEASE_VERSION__ !== 'undefined') {
        return __RELEASE_VERSION__;
      }
      // Fallback to environment variable
      return import.meta.env.VITE_RELEASE_VERSION || undefined;
    })(),

    // Swiss data protection compliance configuration
    beforeSend(event, hint) {
      // Filter out known development/testing errors
      if (import.meta.env.DEV) {
        // Don't send console errors in development
        if (hint.originalException instanceof Error &&
            hint.originalException.message.includes('Development')) {
          return null;
        }
      }

      // Swiss data protection compliance - scrub PII from events
      if (event.user) {
        delete event.user.ip_address;
        delete event.user.email;
        delete event.user.username;
        // Keep only user.id for debugging correlation
      }

      // Scrub sensitive legal data from error messages and contexts
      if (event.exception?.values) {
        event.exception.values.forEach(exception => {
          if (exception.value) {
            // Remove CHE numbers, names, and other legal identifiers
            exception.value = exception.value
              .replace(/CHE-\d{3}\.\d{3}\.\d{3}/g, '[CHE-REDACTED]')
              .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL-REDACTED]')
              .replace(/\b(?:AG|GmbH|Stiftung|Verein|Genossenschaft)\s+[A-Za-zÄÖÜäöüß\s.-]+/g, '[ENTITY-NAME-REDACTED]');
          }
        });
      }

      // Add legal workflow context without exposing sensitive data
      if (event.contexts) {
        const legalContext = {
          workflow_stage: event.contexts.workflow?.stage,
          entity_type: event.contexts.entity?.legal_form,
          document_type: event.contexts.document?.type,
          // Exclude sensitive fields like names, UIDs, content
        };
        event.contexts.legal_workflow = legalContext;
      }

      return event;
    },

    // Performance monitoring configuration
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0.5,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Sentry logger instance for structured logging
 */
export const logger = Sentry.logger;

/**
 * Custom error boundary component for React error handling
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Utility function to capture exceptions with context
 */
export function captureException(error: Error, context?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
    }
    Sentry.captureException(error);
  });
}

/**
 * Utility function to create custom performance spans
 */
export function withPerformanceSpan<T>(
  operation: string,
  description: string,
  fn: (span: Sentry.Span) => T | Promise<T>
): T | Promise<T> {
  return Sentry.startSpan(
    {
      op: operation,
      name: description,
    },
    fn
  );
}

/**
 * Swiss Legal Workflow Monitoring
 * Specialized error tracking and performance monitoring for legal operations
 */

/**
 * Monitor Swiss entity operations with compliance context
 */
export function captureEntityException(
  error: Error,
  entityContext: {
    operation: 'create' | 'update' | 'delete' | 'view';
    legal_form?: string;
    canton?: string;
    tenant_id?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('operation_type', 'swiss_entity');
    scope.setTag('entity_operation', entityContext.operation);

    if (entityContext.legal_form) {
      scope.setTag('legal_form', entityContext.legal_form);
    }
    if (entityContext.canton) {
      scope.setTag('canton', entityContext.canton);
    }
    if (entityContext.tenant_id) {
      scope.setContext('tenant', { id: entityContext.tenant_id });
    }

    scope.setLevel('error');
    Sentry.captureException(error);
  });
}

/**
 * Monitor document approval workflow operations
 */
export function captureApprovalWorkflowException(
  error: Error,
  workflowContext: {
    operation: 'start' | 'approve' | 'reject' | 'escalate';
    workflow_id?: string;
    step_type?: string;
    document_type?: string;
    tenant_id?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('operation_type', 'approval_workflow');
    scope.setTag('workflow_operation', workflowContext.operation);

    if (workflowContext.step_type) {
      scope.setTag('workflow_step', workflowContext.step_type);
    }
    if (workflowContext.document_type) {
      scope.setTag('document_type', workflowContext.document_type);
    }
    if (workflowContext.tenant_id) {
      scope.setContext('tenant', { id: workflowContext.tenant_id });
    }

    scope.setLevel('error');
    Sentry.captureException(error);
  });
}

/**
 * Monitor compliance validation operations
 */
export function captureComplianceException(
  error: Error,
  complianceContext: {
    validation_type: 'uid' | 'legal_form' | 'canton' | 'document' | 'workflow';
    validation_rule?: string;
    entity_id?: string;
    tenant_id?: string;
  }
) {
  Sentry.withScope((scope) => {
    scope.setTag('operation_type', 'compliance_validation');
    scope.setTag('validation_type', complianceContext.validation_type);

    if (complianceContext.validation_rule) {
      scope.setTag('validation_rule', complianceContext.validation_rule);
    }
    if (complianceContext.tenant_id) {
      scope.setContext('tenant', { id: complianceContext.tenant_id });
    }

    scope.setLevel('warning');
    Sentry.captureException(error);
  });
}

/**
 * Performance monitoring for critical Swiss legal operations
 */
export function withEntityPerformanceSpan<T>(
  operation: 'entity_create' | 'entity_update' | 'entity_validate' | 'entity_search',
  entityType: string,
  fn: (span: Sentry.Span) => T | Promise<T>
): T | Promise<T> {
  return Sentry.startSpan(
    {
      op: 'swiss_entity',
      name: `${operation}.${entityType}`,
      attributes: {
        'entity.operation': operation,
        'entity.type': entityType,
      },
    },
    fn
  );
}

export function withApprovalPerformanceSpan<T>(
  operation: 'workflow_start' | 'workflow_approve' | 'workflow_reject' | 'document_validate',
  workflowType: string,
  fn: (span: Sentry.Span) => T | Promise<T>
): T | Promise<T> {
  return Sentry.startSpan(
    {
      op: 'approval_workflow',
      name: `${operation}.${workflowType}`,
      attributes: {
        'workflow.operation': operation,
        'workflow.type': workflowType,
      },
    },
    fn
  );
}

/**
 * Database operation monitoring with Swiss legal context
 */
export function withDatabasePerformanceSpan<T>(
  operation: 'query' | 'transaction' | 'migration',
  table: string,
  fn: (span: Sentry.Span) => T | Promise<T>
): T | Promise<T> {
  return Sentry.startSpan(
    {
      op: 'db',
      name: `db.${operation}.${table}`,
      attributes: {
        'db.operation': operation,
        'db.table': table,
        'db.system': 'neon_postgresql',
      },
    },
    fn
  );
}

